import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DisplayPredictions from "./components/DisplayPredictions";
import StructuredJSONDisplay from "./components/StructuredJSONDisplay";
import ChemistryDisplay from "./components/ChemistryDisplay";
import DocumentUpload from "../../modals/DocumentUpload";
import { Tooltip } from "./components/Tooltip";
import { useZoom } from "../../hooks/useZoom";
import { usePan } from "../../hooks/usePan";
import {
  transformToLegacyFormat,
  transformToPositionedFormat,
  extractLineStructuring,
} from "../../utils/transformData";
import {
  getDocument,
  deletePage,
  getDocumentPages,
  deleteDocument,
  renameDocument,
  getPageData,
  getPageImage,
} from "../../utils/Api";

import {
  FilePlus,
  Pencil,
  Braces,
  Trash,
  Search,
  ChevronUp,
  ChevronDown,
  X,
  Download,
  House,
  ZoomIn,
  ZoomOut,
  SlidersHorizontal,
} from "lucide-react";

import UserNavbar from "../../components/UserNavbar";
import DeleteDocumentModal from "../../modals/DeleteDocumentModal";
import DeletePageModal from "../../modals/DeletePageModal";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import { useUpload } from "../../contexts/UploadContext";

interface DocumentData {
  name: string;
  [key: string]: any;
}

interface PageData {
  id: string;
  status: string;
}

type PagesData = PageData[];

// ─── Lazy page reveal ───────────────────────────────────────────────────────
/**
 * Defers mounting heavy page content until the container scrolls near the
 * viewport. A lightweight min-height placeholder keeps scroll-sync accurate.
 */
const LazyPage: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      ([entry]) => {
        if (entry!.isIntersecting) {
          setVisible(true);
          ob.disconnect();
        }
      },
      { rootMargin: "500px" },
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, []);

  return (
    <div ref={ref} className="min-h-[860px]">
      {visible ? children : null}
    </div>
  );
};

// ─── Memoised left-panel page ────────────────────────────────────────────────
const LeftPageItem = React.memo<{
  documentID: string;
  pageId: string;
  index: number;
  zoom: number;
  panCursor: string;
  panHandlers: { onPointerDown: (e: React.PointerEvent<HTMLElement>) => void };
  selectedTerm: string;
  onHoverObject: (uuid: string | null) => void;
  hoveredObjectUuid: string | null;
  onDeletePage: (index: number) => void;
}>(
  ({
    documentID,
    pageId,
    index,
    zoom,
    panCursor,
    panHandlers,
    selectedTerm,
    onHoverObject,
    hoveredObjectUuid,
    onDeletePage,
  }) => (
    <div className="px-2 md:px-5 relative">
      <div className="mb-2 text-sm font-semibold text-slate-600 text-center flex items-center justify-center gap-2">
        <span>Page {index + 1}</span>
        <button
          onClick={() => onDeletePage(index)}
          className="p-1 hover:bg-red-100 rounded transition-colors"
          title="Delete this page"
        >
          <Trash size={14} className="text-red-600" />
        </button>
      </div>
      <div
        className="h-[60vh] md:h-[800px] overflow-auto"
        style={{ cursor: panCursor }}
        {...panHandlers}
      >
        <div
          style={{
            width: `${zoom * 100}%`,
            height: `${zoom * 100}%`,
            minWidth: "100%",
            minHeight: "100%",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: `${(1 / zoom) * 100}%`,
              height: `${(1 / zoom) * 100}%`,
              transform: `scale(${zoom})`,
              transformOrigin: "top left",
            }}
          >
            <DisplayPredictions
              documentID={documentID}
              pageID={pageId}
              mode="image-only"
              selectedTerm={selectedTerm}
              onHoverObject={onHoverObject}
              highlightedObjectUuid={hoveredObjectUuid}
            />
          </div>
        </div>
      </div>
    </div>
  ),
);
LeftPageItem.displayName = "LeftPageItem";

// ─── Memoised right-panel positioned page ───────────────────────────────────
const RightPageItem = React.memo<{
  documentID: string;
  pageId: string;
  index: number;
  zoom: number;
  panCursor: string;
  panHandlers: { onPointerDown: (e: React.PointerEvent<HTMLElement>) => void };
  activeSearchQuery: string;
  isCurrentSearchResult: boolean;
  currentOccurrenceIndex: number | undefined;
  selectedTerm: string;
  onTermSelect: (term: string) => void;
  hoveredParentUuid: string | null;
  pageRef: (el: HTMLDivElement | null) => void;
  structuredPageRef: (el: HTMLDivElement | null) => void;
}>(
  ({
    documentID,
    pageId,
    index,
    zoom,
    panCursor,
    panHandlers,
    activeSearchQuery,
    isCurrentSearchResult,
    currentOccurrenceIndex,
    selectedTerm,
    onTermSelect,
    hoveredParentUuid,
    pageRef,
    structuredPageRef,
  }) => (
    <div
      ref={pageRef}
      className="border-b border-slate-200 last:border-b-0 group"
    >
      <div className="bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700 sticky top-0 z-10 border-b border-slate-200 flex items-center justify-between">
        <span>Page {index + 1}</span>
      </div>
      <div
        ref={structuredPageRef}
        className="h-[60vh] md:h-[800px] overflow-auto"
        style={{ cursor: panCursor }}
        {...panHandlers}
      >
        <div
          style={{
            width: `${zoom * 100}%`,
            height: `${zoom * 100}%`,
            minWidth: "100%",
            minHeight: "100%",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: `${(1 / zoom) * 100}%`,
              height: `${(1 / zoom) * 100}%`,
              transform: `scale(${zoom})`,
              transformOrigin: "top left",
            }}
            className="px-2 md:px-5 py-3 md:py-5"
          >
            <StructuredJSONDisplayWrapper
              documentID={documentID}
              pageId={pageId}
              {...(activeSearchQuery && { searchQuery: activeSearchQuery })}
              {...(isCurrentSearchResult &&
                typeof currentOccurrenceIndex === "number" && {
                  isCurrentSearchResult: true,
                  currentOccurrenceIndex,
                })}
              {...(selectedTerm && { selectedTerm })}
              onTermSelect={onTermSelect}
              hoveredParentUuid={hoveredParentUuid}
            />
          </div>
        </div>
      </div>
    </div>
  ),
);
RightPageItem.displayName = "RightPageItem";

const StructuredJSONDisplayWrapper: React.FC<{
  documentID: string;
  pageId: string;
  searchQuery?: string;
  isCurrentSearchResult?: boolean;
  currentOccurrenceIndex?: number;
  selectedTerm?: string;
  onTermSelect?: (term: string) => void;
  hoveredParentUuid?: string | null;
}> = ({
  documentID,
  pageId,
  searchQuery,
  isCurrentSearchResult,
  currentOccurrenceIndex,
  selectedTerm,
  onTermSelect,
  hoveredParentUuid,
}) => {
  const [rawData, setRawData] = useState<any>(null);
  const [readableData, setReadableData] = useState<any>(null);
  const [imageHeight, setImageHeight] = useState<number>(800);
  const [imageWidth, setImageWidth] = useState<number>(600);

  useEffect(() => {
    let mounted = true;
    let img: HTMLImageElement | null = null;

    const fetchData = async () => {
      try {
        const { data } = await getPageData(documentID, pageId);
        console.log(
          `[StructuredJSONDisplayWrapper] page ${pageId} readable_data keys:`,
          data.readable_data
            ? Object.keys(data.readable_data)
            : "null/undefined",
        );
        const transformed = transformToPositionedFormat(data.readable_data);
        console.log(
          `[StructuredJSONDisplayWrapper] page ${pageId} transformed textLines:`,
          transformed?.textLines?.length ?? "none",
          "molecules:",
          transformed?.molecules?.length ?? "none",
        );
        // Use readable_data (structured output) for positioned view
        if (mounted) {
          setRawData(transformed);
          setReadableData(data.readable_data);
        }
      } catch (error) {
        console.error(`Error fetching data for page ${pageId}:`, error);
      }
    };

    const fetchImage = async () => {
      try {
        const { data } = await getPageImage(documentID, pageId);
        img = new Image();
        img.onload = () => {
          if (mounted) {
            setImageHeight(img!.naturalHeight);
            setImageWidth(img!.naturalWidth);
          }
        };
        img.src = data.image;
      } catch (error) {
        console.error(`Error fetching image for page ${pageId}:`, error);
      }
    };

    fetchData();
    fetchImage();

    return () => {
      mounted = false;
      if (img) {
        img.onload = null;
      }
    };
  }, [documentID, pageId]);

  if (!rawData || !readableData) {
    return <div className="p-4 text-slate-500">Loading page data…</div>;
  }

  // Transparent fallback if the data loaded but has no positional content
  if (!rawData.textLines?.length && !rawData.molecules?.length) {
    console.warn(
      `[StructuredJSONDisplayWrapper] page ${pageId} has rawData but no textLines/molecules. rawData:`,
      rawData,
    );
    return (
      <div className="p-4 text-slate-400 text-sm">
        No structured content available for this page.
      </div>
    );
  }

  return (
    <StructuredJSONDisplay
      rawData={rawData}
      readableData={readableData}
      imageHeight={imageHeight}
      imageWidth={imageWidth}
      {...(searchQuery !== undefined && { searchQuery })}
      {...(isCurrentSearchResult !== undefined && { isCurrentSearchResult })}
      {...(currentOccurrenceIndex !== undefined && { currentOccurrenceIndex })}
      {...(selectedTerm !== undefined && { selectedTerm })}
      {...(onTermSelect && { onTermSelect })}
      {...(hoveredParentUuid !== undefined && { hoveredParentUuid })}
    />
  );
};

/** Fetches positional data for all pages and renders ChemistryDisplay. */
const ChemistryDataLoader: React.FC<{
  documentID: string;
  pages: string[];
  onHoverObject?: (uuid: string | null) => void;
  hoveredObjectUuid?: string | null;
  onScrollToPage?: (pageIndex: number) => void;
}> = ({
  documentID,
  pages,
  onHoverObject,
  hoveredObjectUuid,
  onScrollToPage,
}) => {
  const [pagesData, setPagesData] = useState<
    { pageId: string; rawData: any }[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      const results = await Promise.all(
        pages.map(async (pageId) => {
          try {
            const { data } = await getPageData(documentID, pageId);
            return {
              pageId,
              rawData: transformToPositionedFormat(data.readable_data),
            };
          } catch {
            return { pageId, rawData: null };
          }
        }),
      );
      if (mounted) {
        setPagesData(results);
        setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [pages]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-slate-400">
        Loading chemistry data…
      </div>
    );
  }

  return (
    <ChemistryDisplay
      pagesData={pagesData}
      {...(onHoverObject !== undefined && { onHoverObject })}
      {...(hoveredObjectUuid !== undefined && { hoveredObjectUuid })}
      {...(onScrollToPage !== undefined && { onScrollToPage })}
    />
  );
};

const View: React.FC = () => {
  const { id: documentID } = useParams<{ id: string }>();
  const [documentName, setDocumentName] = useState<string>("Loading...");
  const [documentData, setDocumentData] = useState<DocumentData | null>(null);
  const [pages, setPages] = useState<string[]>([]);
  const [isAddPagesModalOpen, setIsAddPagesModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletePageModalOpen, setIsDeletePageModalOpen] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingPage, setIsDeletingPage] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState<string>("");
  const [, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [activeSearchQuery, setActiveSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<
    Array<{ pageId: string; occurrenceIndex: number }>
  >([]);
  const [currentResultIndex, setCurrentResultIndex] = useState<number>(0);
  const [selectedTerm, setSelectedTerm] = useState<string>("");
  const [activeTab, setActiveTab] = useState<
    "positioned" | "chemistry" | "json"
  >("positioned");
  const [hoveredObjectUuid, setHoveredObjectUuid] = useState<string | null>(
    null,
  );
  const [bboxVisibility, setBboxVisibility] = useState({
    text: true,
    molecules: true,
    tables: false,
  });
  const [showBboxMenu, setShowBboxMenu] = useState(false);
  const bboxMenuRef = useRef<HTMLDivElement>(null);
  const [allPagesJsonData, setAllPagesJsonData] = useState<any>(null);
  const [allPagesRawData, setAllPagesRawData] = useState<any[]>([]);
  const [mobileView, setMobileView] = useState<"image" | "data">("image");
  const [canDownload, setCanDownload] = useState<boolean>(true);
  const navigate = useNavigate();
  const { uploads } = useUpload();

  const leftZoom = useZoom();
  const rightZoom = useZoom();
  const leftPan = usePan(leftZoom.zoom > 1);
  const rightPan = usePan(rightZoom.zoom > 1);

  const leftScrollRef = useRef<HTMLDivElement>(null);
  const rightScrollRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<{ [key: string]: HTMLDivElement }>({});
  const structuredPageRefs = useRef<{ [key: string]: HTMLDivElement }>({});
  const leftPageRefs = useRef<{ [key: string]: HTMLDivElement }>({});
  const isScrolling = useRef<{ left: boolean; right: boolean }>({
    left: false,
    right: false,
  });
  const scrollRafRef = useRef<{ left: number | null; right: number | null }>({
    left: null,
    right: null,
  });

  const fetchDocumentPages = async () => {
    if (!documentID) return;

    try {
      const { data: pagesData }: { data: PagesData } =
        await getDocumentPages(documentID);
      const pageIds = pagesData.map((page) => page.id);
      setPages(pageIds);
    } catch (error) {
      console.error("Error fetching pages:", error);
    }
  };

  // Close bbox menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        bboxMenuRef.current &&
        !bboxMenuRef.current.contains(e.target as Node)
      ) {
        setShowBboxMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!documentID) return;

    let cancelled = false;

    const fetchDocument = async () => {
      try {
        const { data: document }: { data: DocumentData } =
          await getDocument(documentID);
        if (!cancelled) {
          setDocumentName(document.name);
          setDocumentData(document);
          setCanDownload(document.can_download ?? true);
        }

        const { data: pagesData }: { data: PagesData } =
          await getDocumentPages(documentID);
        if (!cancelled) {
          const pageIds = pagesData.map((page) => page.id);
          setPages(pageIds);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching document:", error);
        }
      }
    };

    fetchDocument();

    return () => {
      cancelled = true;
    };
  }, [documentID]);

  const processedUploadIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!documentID) return;

    const completedUpload = uploads.find(
      (u) =>
        u.documentId === documentID &&
        u.status === "complete" &&
        u.mode === "add" &&
        !processedUploadIds.current.has(u.id),
    );

    if (completedUpload) {
      processedUploadIds.current.add(completedUpload.id);
      fetchDocumentPages();
    }
  }, [uploads, documentID]);

  const handleScroll = (source: "left" | "right") => {
    const targetFlag = source === "left" ? "right" : "left";
    // Skip if the opposite panel drove this scroll (avoid ping-pong loop)
    if (isScrolling.current[targetFlag]) return;
    // Coalesce rapid scroll events into a single rAF callback
    if (scrollRafRef.current[source] !== null) return;

    const sourceRef = source === "left" ? leftScrollRef : rightScrollRef;
    const targetRef = source === "left" ? rightScrollRef : leftScrollRef;

    scrollRafRef.current[source] = requestAnimationFrame(() => {
      scrollRafRef.current[source] = null;
      if (
        !sourceRef.current ||
        !targetRef.current ||
        isScrolling.current[targetFlag]
      )
        return;

      isScrolling.current[source] = true;

      const scrollTop = sourceRef.current.scrollTop;
      const scrollHeight =
        sourceRef.current.scrollHeight - sourceRef.current.clientHeight;
      const scrollRatio = scrollHeight > 0 ? scrollTop / scrollHeight : 0;

      const targetScrollHeight =
        targetRef.current.scrollHeight - targetRef.current.clientHeight;
      targetRef.current.scrollTop = scrollRatio * targetScrollHeight;

      setTimeout(() => {
        isScrolling.current[source] = false;
      }, 50);
    });
  };

  const confirmDelete = async () => {
    if (!documentID) {
      setError("Document ID missing");
      return;
    }
    setIsDeleting(true);
    setError(null);

    try {
      await deleteDocument(documentID);
      navigate("/home/dashboard");
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to delete document");
      setIsDeleting(false);
    }
  };

  const handleDeletePageClick = (pageIndex: number) => {
    setCurrentPageIndex(pageIndex);
    setIsDeletePageModalOpen(true);
  };

  const confirmDeletePage = async () => {
    if (!documentID) {
      setError("Document ID missing");
      return;
    }

    if (currentPageIndex < 0 || currentPageIndex >= pages.length) {
      setError("Invalid page selection");
      return;
    }

    setIsDeletingPage(true);
    setError(null);

    try {
      if (pages.length === 1) {
        await deleteDocument(documentID);
        navigate("/home/dashboard");
        return;
      }

      const pageIdToDelete = pages[currentPageIndex]!;
      await deletePage(documentID!, pageIdToDelete);
      setPages((prevPages) =>
        prevPages.filter((_, index) => index !== currentPageIndex),
      );
      setIsDeletePageModalOpen(false);
      setIsDeletingPage(false);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to delete page");
      setIsDeletingPage(false);
    }
  };

  const handleAddPagesModalClose = (isOpen: boolean) => {
    setIsAddPagesModalOpen(isOpen);

    if (!isOpen) {
      fetchDocumentPages();
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingName(true);
    setEditingName(documentName);
  };

  const handleSaveEdit = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!documentID) {
      setError("Document ID missing");
      return;
    }
    setError(null);

    try {
      await renameDocument(documentID, editingName);
      setDocumentName(editingName);
      setIsEditingName(false);
      setEditingName("");
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to rename document");
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingName(false);
    setEditingName("");
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || pages.length === 0) return;

    const query = searchQuery.toLowerCase();
    const allOccurrences: Array<{ pageId: string; occurrenceIndex: number }> =
      [];

    for (let i = 0; i < pages.length; i++) {
      const pageId = pages[i];
      try {
        const { data } = await getPageData(documentID!, pageId!);
        const readableDataString = JSON.stringify(
          data.readable_data,
        ).toLowerCase();

        let position = 0;
        let occurrenceIndex = 0;
        while (
          (position = readableDataString.indexOf(query, position)) !== -1
        ) {
          allOccurrences.push({ pageId: pageId!, occurrenceIndex });
          position += query.length;
          occurrenceIndex++;
        }
      } catch (error) {
        console.error(`Error searching page ${pageId}:`, error);
      }
    }

    setSearchResults(allOccurrences);
    setCurrentResultIndex(0);
    setActiveSearchQuery(searchQuery);

    if (allOccurrences.length > 0) {
      setTimeout(() => {
        scrollToResult(allOccurrences[0]!.pageId);
      }, 150);
    }
  };

  const scrollToResult = (pageId: string) => {
    const pageElement = pageRefs.current[pageId];
    if (pageElement && rightScrollRef.current) {
      isScrolling.current.right = true;

      pageElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      setTimeout(() => {
        isScrolling.current.right = false;
      }, 600);
    }
  };

  /** Scroll the left (original image) panel to a specific page index. */
  const scrollToLeftPage = useCallback(
    (pageIndex: number) => {
      const pageId = pages[pageIndex];
      if (!pageId) return;
      const el = leftPageRefs.current[pageId];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    [pages],
  );

  const navigateToNextResult = () => {
    if (searchResults.length === 0) return;
    const nextIndex = (currentResultIndex + 1) % searchResults.length;
    setCurrentResultIndex(nextIndex);
    scrollToResult(searchResults[nextIndex]!.pageId);
  };

  const navigateToPrevResult = () => {
    if (searchResults.length === 0) return;
    const prevIndex =
      currentResultIndex === 0
        ? searchResults.length - 1
        : currentResultIndex - 1;
    setCurrentResultIndex(prevIndex);
    scrollToResult(searchResults[prevIndex]!.pageId);
  };

  const clearSearch = () => {
    setSearchResults([]);
    setCurrentResultIndex(0);
    setActiveSearchQuery("");
  };

  const fetchAllPagesJson = async () => {
    if (!documentID || pages.length === 0) {
      return null;
    }

    try {
      const pagesDataPromises = pages.map(async (pageId, index) => {
        try {
          const { data } = await getPageData(documentID, pageId);
          return {
            page_number: index + 1,
            page_id: pageId,
            raw_data: data.raw_data,
            readable_data: data.readable_data,
          };
        } catch (error) {
          console.error(`Error fetching data for page ${pageId}:`, error);
          return {
            page_number: index + 1,
            page_id: pageId,
            error: "Failed to fetch page data",
          };
        }
      });

      const allPagesData = await Promise.all(pagesDataPromises);

      return {
        document_name: documentName,
        document_id: documentID,
        total_pages: pages.length,
        pages: allPagesData,
      };
    } catch (err: any) {
      console.error("Error fetching JSON:", err);
      setError(err.message || "Failed to fetch JSON");
      return null;
    }
  };

  const handleToggleJsonView = async () => {
    if (activeTab !== "json" && !allPagesJsonData) {
      const data = await fetchAllPagesJson();
      setAllPagesJsonData(data);

      if (data?.pages) {
        setAllPagesRawData(data.pages.map((p: any) => p.raw_data));
      }
    }
    setActiveTab("json");
  };

  const handleToggleStructuredView = async () => {
    if (activeTab !== "positioned" && !allPagesJsonData) {
      const data = await fetchAllPagesJson();
      setAllPagesJsonData(data);

      if (data?.pages) {
        setAllPagesRawData(data.pages.map((p: any) => p.raw_data));
      }
    }
    setActiveTab("positioned");
  };

  const handleToggleChemistryView = async () => {
    if (activeTab !== "chemistry" && !allPagesJsonData) {
      const data = await fetchAllPagesJson();
      setAllPagesJsonData(data);

      if (data?.pages) {
        setAllPagesRawData(data.pages.map((p: any) => p.raw_data));
      }
    }
    setActiveTab("chemistry");
  };

  const handleExportJson = async () => {
    if (!documentID || pages.length === 0) {
      setError("No data to export");
      return;
    }

    try {
      const exportData = allPagesJsonData || (await fetchAllPagesJson());

      if (!exportData) {
        setError("Failed to fetch data for export");
        return;
      }

      const exportDataWithTimestamp = {
        document_name: exportData.document_name,
        total_pages: exportData.total_pages,
        exported_at: new Date().toISOString(),
        pages: (exportData.pages ?? []).map((p: any) => ({
          page_number: p.page_number,
          lines: extractLineStructuring(p.raw_data),
        })),
      };

      const blob = new Blob(
        [JSON.stringify(exportDataWithTimestamp, null, 2)],
        {
          type: "application/json",
        },
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${documentName.replace(/[^a-z0-9]/gi, "_")}_export.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Error exporting JSON:", err);
      setError(err.message || "Failed to export JSON");
    }
  };

  const handleDownloadPageJson = async (pageId: string, pageIndex: number) => {
    if (!documentID) {
      setError("Document ID missing");
      return;
    }

    try {
      const { data } = await getPageData(documentID, pageId);

      const pageData = {
        document_name: documentName,
        page_number: pageIndex + 1,
        exported_at: new Date().toISOString(),
        lines: extractLineStructuring(data.raw_data),
      };

      const blob = new Blob([JSON.stringify(pageData, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${documentName.replace(/[^a-z0-9]/gi, "_")}_page_${
        pageIndex + 1
      }.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(`Error downloading page ${pageIndex + 1} JSON:`, err);
      setError(err.message || "Failed to download page JSON");
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <UserNavbar />

      {/* Mobile tab switcher */}
      <div className="md:hidden flex bg-[#303a4a] border-b border-slate-600">
        <button
          onClick={() => setMobileView("image")}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            mobileView === "image"
              ? "bg-blue-600 text-white"
              : "text-[#d6d9da] hover:bg-[#3d4552]"
          }`}
        >
          Document
        </button>
        <button
          onClick={() => setMobileView("data")}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            mobileView === "data"
              ? "bg-blue-600 text-white"
              : "text-[#d6d9da] hover:bg-[#3d4552]"
          }`}
        >
          Extracted Data
        </button>
      </div>

      <div className="flex flex-col md:flex-row bg-slate-50 text-slate-800 font-sans flex-1 overflow-hidden">
        {/* left half */}
        <div
          className={`w-full md:w-1/2 flex flex-col border-r border-slate-200 overflow-hidden ${mobileView !== "image" ? "hidden md:flex" : "flex"}`}
        >
          {/* toolbar */}
          <div className="bg-[#303a4a] border-b border-slate-200 py-1 px-2 md:px-4 flex items-center justify-between gap-2">
            <div className="flex items-center space-x-2 md:space-x-3 min-w-0 flex-1">
              <div className="flex items-center space-x-2 pr-2 md:pr-3 border-r border-gray-600 shrink-0">
                <Tooltip text="Go back" position="right">
                  <button
                    onClick={() => navigate(-1)}
                    className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors"
                  >
                    <House size={18} className="md:w-5 md:h-5 text-[#d6d9da]" />
                  </button>
                </Tooltip>
              </div>

              {isEditingName ? (
                <div className="flex items-center space-x-2 min-w-0 flex-1">
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm min-w-0 flex-1"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveEdit}
                    className="text-green-500 hover:text-green-600 font-medium text-sm shrink-0"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="text-white hover:text-gray-200 font-medium text-sm shrink-0"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <h1
                    className="font-medium text-slate-700 truncate text-white text-sm md:text-base min-w-0"
                    title={documentName}
                  >
                    {documentName}
                  </h1>
                  <Tooltip text="Rename" position="right">
                    <button
                      onClick={handleEditClick}
                      className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors shrink-0"
                    >
                      <Pencil
                        size={16}
                        className="md:w-[18px] md:h-[18px] text-[#d6d9da]"
                      />
                    </button>
                  </Tooltip>
                </>
              )}
            </div>

            <div className="flex items-center space-x-1 md:space-x-2 shrink-0">
              {/* bbox visibility dropdown */}
              <div className="relative" ref={bboxMenuRef}>
                <Tooltip text="Toggle overlays" position="left">
                  <button
                    onClick={() => setShowBboxMenu((v) => !v)}
                    className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors"
                  >
                    <SlidersHorizontal
                      size={16}
                      className="md:w-[18px] md:h-[18px] text-[#d6d9da]"
                    />
                  </button>
                </Tooltip>
                {showBboxMenu && (
                  <div className="absolute right-0 top-full mt-1 z-50 bg-[#303a4a] border border-gray-600 rounded-lg shadow-xl py-1 w-48">
                    {(
                      [
                        ["text", "Text"],
                        ["molecules", "Molecules"],
                        ["tables", "Tables"],
                      ] as const
                    ).map(([key, label]) => (
                      <label
                        key={key}
                        className="flex items-center justify-between px-3 py-2 hover:bg-[#3d4552] cursor-pointer select-none"
                      >
                        <span className="text-sm text-[#d6d9da]">{label}</span>
                        <div
                          onClick={() =>
                            setBboxVisibility((v) => ({ ...v, [key]: !v[key] }))
                          }
                          className={`relative w-9 h-5 rounded-full transition-colors ${
                            bboxVisibility[key] ? "bg-blue-500" : "bg-gray-600"
                          }`}
                        >
                          <div
                            className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                              bboxVisibility[key]
                                ? "translate-x-4"
                                : "translate-x-0"
                            }`}
                          />
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* zoom controls */}
              <div className="flex items-center space-x-0.5 border-r border-gray-600 pr-1 md:pr-2 mr-0.5">
                <Tooltip text="Zoom out" position="left">
                  <button
                    onClick={leftZoom.zoomOut}
                    disabled={!leftZoom.canZoomOut}
                    className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ZoomOut
                      size={16}
                      className="md:w-[18px] md:h-[18px] text-[#d6d9da]"
                    />
                  </button>
                </Tooltip>
                <button
                  onClick={leftZoom.resetZoom}
                  className="text-[#d6d9da] text-xs font-mono w-10 text-center hover:bg-[#3d4552] rounded px-1 py-0.5 transition-colors"
                  title="Reset zoom"
                >
                  {Math.round(leftZoom.zoom * 100)}%
                </button>
                <Tooltip text="Zoom in" position="left">
                  <button
                    onClick={leftZoom.zoomIn}
                    disabled={!leftZoom.canZoomIn}
                    className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ZoomIn
                      size={16}
                      className="md:w-[18px] md:h-[18px] text-[#d6d9da]"
                    />
                  </button>
                </Tooltip>
              </div>

              <Tooltip text="Add pages" position="left">
                <button
                  onClick={() => setIsAddPagesModalOpen(true)}
                  className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors"
                >
                  <FilePlus
                    size={18}
                    className="md:w-5 md:h-5 text-[#d6d9da]"
                  />
                </button>
              </Tooltip>

              <Tooltip text="Delete document" position="left">
                <button
                  onClick={() => setIsDeleteModalOpen(true)}
                  disabled={isDeleting}
                  className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors"
                >
                  <Trash size={18} className="md:w-5 md:h-5 text-[#d6d9da]" />
                </button>
              </Tooltip>
            </div>
          </div>

          {/* image display */}
          <div
            ref={leftScrollRef}
            className="flex-grow bg-slate-100 overflow-y-auto"
            onScroll={() => handleScroll("left")}
          >
            <div className="space-y-4 md:space-y-8 py-3 md:py-5">
              {pages.map((pageId, index) => (
                <div
                  key={pageId}
                  ref={(el) => {
                    if (el) leftPageRefs.current[pageId] = el;
                  }}
                  className="px-2 md:px-5 relative"
                >
                  {/* page header — never zoomed */}
                  <div className="mb-2 text-sm font-semibold text-slate-600 text-center flex items-center justify-center gap-2">
                    <span>Page {index + 1}</span>

                    <button
                      onClick={() => handleDeletePageClick(index)}
                      className="p-1 hover:bg-red-100 rounded transition-colors"
                      title="Delete this page"
                    >
                      <Trash size={14} className="text-red-600" />
                    </button>
                  </div>

                  {/* pannable + zoomable content */}
                  <div
                    className="h-[60vh] md:h-[800px] overflow-auto"
                    style={{ cursor: leftPan.cursor }}
                    {...leftPan.panHandlers}
                  >
                    {/* spacer grows with zoom → real scrollable area */}
                    <div
                      style={{
                        width: `${leftZoom.zoom * 100}%`,
                        height: `${leftZoom.zoom * 100}%`,
                        minWidth: "100%",
                        minHeight: "100%",
                        position: "relative",
                      }}
                    >
                      {/* content: 1/zoom of spacer = original container size, then scaled visually */}
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: `${(1 / leftZoom.zoom) * 100}%`,
                          height: `${(1 / leftZoom.zoom) * 100}%`,
                          transform: `scale(${leftZoom.zoom})`,
                          transformOrigin: "top left",
                        }}
                      >
                        <DisplayPredictions
                          documentID={documentID!}
                          pageID={pageId}
                          mode="image-only"
                          selectedTerm={selectedTerm}
                          onHoverObject={setHoveredObjectUuid}
                          highlightedObjectUuid={hoveredObjectUuid}
                          bboxVisibility={bboxVisibility}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* right half */}
        <div
          className={`w-full md:w-1/2 flex flex-col overflow-hidden ${mobileView !== "data" ? "hidden md:flex" : "flex"}`}
        >
          {/* toolbar */}
          <div className="bg-[#303a4a] border-b border-slate-200 py-1 px-2 md:px-4 flex items-center justify-between gap-2">
            <div className="flex items-center space-x-2 md:space-x-3 min-w-0">
              <h1
                className="font-medium text-slate-700 truncate text-white text-sm md:text-base hidden sm:block"
                title={documentName}
              >
                Extracted Data
              </h1>
            </div>
            <div className="flex items-center space-x-2 md:space-x-3">
              {/* zoom controls */}
              <div className="flex items-center space-x-0.5 border-r border-gray-600 pr-1 md:pr-2">
                <Tooltip text="Zoom out" position="left">
                  <button
                    onClick={rightZoom.zoomOut}
                    disabled={!rightZoom.canZoomOut}
                    className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ZoomOut
                      size={16}
                      className="md:w-[18px] md:h-[18px] text-[#d6d9da]"
                    />
                  </button>
                </Tooltip>
                <button
                  onClick={rightZoom.resetZoom}
                  className="text-[#d6d9da] text-xs font-mono w-10 text-center hover:bg-[#3d4552] rounded px-1 py-0.5 transition-colors"
                  title="Reset zoom"
                >
                  {Math.round(rightZoom.zoom * 100)}%
                </button>
                <Tooltip text="Zoom in" position="left">
                  <button
                    onClick={rightZoom.zoomIn}
                    disabled={!rightZoom.canZoomIn}
                    className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ZoomIn
                      size={16}
                      className="md:w-[18px] md:h-[18px] text-[#d6d9da]"
                    />
                  </button>
                </Tooltip>
              </div>

              {/* view mode buttons */}
              <div className="flex items-center space-x-0.5 md:space-x-1 bg-slate-700 rounded-md p-0.5 md:p-1">
                <button
                  onClick={handleToggleStructuredView}
                  className={`px-2 md:px-3 py-1 text-xs font-medium rounded transition-colors ${
                    activeTab === "positioned"
                      ? "bg-blue-600 text-white"
                      : "text-[#d6d9da] hover:bg-slate-600"
                  }`}
                >
                  <span className="hidden sm:inline">Preview</span>
                  <span className="sm:hidden">Pre</span>
                </button>
                <button
                  onClick={handleToggleChemistryView}
                  className={`px-2 md:px-3 py-1 text-xs font-medium rounded transition-colors ${
                    activeTab === "chemistry"
                      ? "bg-blue-600 text-white"
                      : "text-[#d6d9da] hover:bg-slate-600"
                  }`}
                >
                  <span className="hidden sm:inline">Chemistry</span>
                  <span className="sm:hidden">Chem</span>
                </button>
                <button
                  onClick={handleToggleJsonView}
                  className={`px-2 md:px-3 py-1 text-xs font-medium rounded transition-colors ${
                    activeTab === "json"
                      ? "bg-blue-600 text-white"
                      : "text-[#d6d9da] hover:bg-slate-600"
                  }`}
                >
                  <span className="hidden sm:inline">Raw JSON</span>
                  <span className="sm:hidden">JSON</span>
                </button>
              </div>

              {canDownload && (
                <Tooltip text="Export JSON" position="left">
                  <button
                    onClick={handleExportJson}
                    className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors"
                  >
                    <Braces
                      size={18}
                      className="md:w-5 md:h-5 text-[#d6d9da]"
                    />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>

          {/* search bar */}
          <div className="bg-white border-b border-slate-200 px-2 md:px-4 py-2 md:py-3">
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                  className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-300 rounded-md
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
        text-sm text-slate-700 placeholder-slate-400"
                />
              </div>

              <PrimaryButton
                onClick={handleSearch}
                disabled={!searchQuery.trim()}
                className="!px-3 md:!px-5 py-2 text-sm font-medium"
              >
                <span className="hidden sm:inline">Search</span>
                <Search size={16} className="sm:hidden" />
              </PrimaryButton>
            </div>

            {/* search component */}
            {searchResults.length > 0 && (
              <div className="mt-2 md:mt-3 flex items-center justify-between gap-2 md:gap-3 bg-slate-50 border border-slate-200 rounded-md px-2 md:px-4 py-2">
                <div className="flex items-center gap-2 md:gap-3">
                  <span className="text-xs md:text-sm text-slate-700 font-medium whitespace-nowrap">
                    {currentResultIndex + 1}/{searchResults.length}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={navigateToPrevResult}
                      className="p-1 md:p-1.5 hover:bg-slate-200 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      disabled={searchResults.length === 0}
                      title="Previous result"
                    >
                      <ChevronUp size={16} className="text-slate-700" />
                    </button>
                    <button
                      onClick={navigateToNextResult}
                      className="p-1 md:p-1.5 hover:bg-slate-200 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      disabled={searchResults.length === 0}
                      title="Next result"
                    >
                      <ChevronDown size={16} className="text-slate-700" />
                    </button>
                  </div>
                </div>
                <button
                  onClick={clearSearch}
                  className="p-1 hover:bg-slate-200 rounded transition-colors"
                  title="Close search results"
                >
                  <X size={16} className="text-slate-600" />
                </button>
              </div>
            )}
          </div>

          {/* display output */}
          <div
            ref={rightScrollRef}
            className="flex-grow overflow-y-auto bg-white"
            onScroll={() => handleScroll("right")}
          >
            {activeTab === "json" ? (
              /* JSON view — single pannable block */
              <div
                className="overflow-auto"
                style={{ cursor: rightPan.cursor }}
                {...rightPan.panHandlers}
              >
                <div
                  style={{
                    width: `${rightZoom.zoom * 100}%`,
                    minWidth: "100%",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      width: `${(1 / rightZoom.zoom) * 100}%`,
                      transform: `scale(${rightZoom.zoom})`,
                      transformOrigin: "top left",
                    }}
                  >
                    <div className="p-4">
                      <pre className="bg-slate-900 text-green-400 p-4 rounded-lg text-sm font-mono overflow-x-auto">
                        {allPagesJsonData
                          ? JSON.stringify(
                              {
                                document_name: allPagesJsonData.document_name,
                                total_pages: allPagesJsonData.total_pages,
                                pages: (allPagesJsonData.pages ?? []).map(
                                  (p: any) => ({
                                    page_number: p.page_number,
                                    lines: extractLineStructuring(p.raw_data),
                                  }),
                                ),
                              },
                              null,
                              2,
                            )
                          : "Loading..."}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            ) : activeTab === "chemistry" ? (
              <ChemistryDataLoader
                documentID={documentID!}
                pages={pages}
                onHoverObject={setHoveredObjectUuid}
                hoveredObjectUuid={hoveredObjectUuid}
                onScrollToPage={scrollToLeftPage}
              />
            ) : (
              <div className="space-y-8">
                {pages.map((pageId, index) => (
                  <div
                    key={pageId}
                    ref={(el) => {
                      if (el) pageRefs.current[pageId] = el;
                    }}
                  >
                    <LazyPage>
                      <RightPageItem
                        documentID={documentID!}
                        pageId={pageId}
                        index={index}
                        zoom={rightZoom.zoom}
                        panCursor={rightPan.cursor}
                        panHandlers={rightPan.panHandlers}
                        activeSearchQuery={activeSearchQuery}
                        isCurrentSearchResult={
                          searchResults.length > 0 &&
                          searchResults[currentResultIndex]?.pageId === pageId
                        }
                        currentOccurrenceIndex={
                          searchResults[currentResultIndex]?.occurrenceIndex
                        }
                        selectedTerm={selectedTerm}
                        onTermSelect={setSelectedTerm}
                        hoveredParentUuid={hoveredObjectUuid}
                        pageRef={() => {}}
                        structuredPageRef={() => {}}
                      />
                    </LazyPage>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* modals */}
      <DeleteDocumentModal
        document={documentData}
        isDeleting={isDeleting}
        onConfirmDelete={confirmDelete}
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      />

      <DeletePageModal
        currentPageIndex={currentPageIndex}
        isDeleting={isDeletingPage}
        onConfirmDelete={confirmDeletePage}
        isOpen={isDeletePageModalOpen}
        onClose={() => setIsDeletePageModalOpen(false)}
        isLastPage={pages.length === 1}
      />

      <DocumentUpload
        mode="add"
        documentID={documentID || null}
        isOpen={isAddPagesModalOpen}
        setIsOpen={handleAddPagesModalClose}
      />
    </div>
  );
};

export default View;
